const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3000';
const TOKEN_KEY = 'nbc.auth.token';

async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...((init?.headers as Record<string, string>) ?? {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;

  const r = await fetch(`${API_BASE_URL}${path}`, { ...init, headers });
  if (r.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
      window.location.assign('/login');
    }
    throw new Error('unauthorized');
  }
  if (!r.ok) {
    const err = await r.text().catch(() => '');
    throw new Error(`API ${r.status}: ${err || r.statusText}`);
  }
  if (r.status === 204) return undefined as T;
  return r.json() as Promise<T>;
}

// ============== Types ==============
export interface Contact {
  id: string;
  lifecycleStage: string;
  temperature: string;
  geoState?: string;
  geoCity?: string;
  firstSeenAt?: string;
  lastSeenAt?: string;
  createdAt: string;
  displayName?: string;
  emailDisplay?: string;
  phoneDisplay?: string;
  utm?: { utm_source?: string; utm_campaign?: string; utm_content?: string };
  scorePurchase?: string | number;
  totalOrders?: string | number;
  openConversations?: string | number;
}

export interface ContactDetail extends Omit<Contact, 'displayName' | 'emailDisplay' | 'phoneDisplay'> {
  email?: string;
  phone?: string;
  fullName?: string;
  geoCountry?: string;
  geoZip?: string;
  fbp?: string;
  fbc?: string;
  externalId?: string;
  metadata?: Record<string, unknown>;
  consents?: Record<string, unknown>;
}

export interface ContactEvent {
  id: string;
  eventType: string;
  sourceSystem: string;
  occurredAt: string;
  payload: Record<string, unknown>;
}

export interface Order {
  id: string;
  gateway: string;
  externalId: string;
  productCode: string;
  productName: string;
  productKind: string;
  amountGrossCents: string | number;
  netCents: string | number;
  paymentMethod: string;
  installments: number;
  status: string;
  approvedAt?: string;
  scheduledReleaseAt?: string;
  releasedAt?: string;
  createdAt: string;
}

export interface Conversation {
  id: string;
  channel: string;
  status: string;
  priority: string;
  tags: string[];
  sentimentScore?: number;
  intentClassification?: string;
  urgencyScore?: number;
  waSessionExpiresAt?: string;
  firstResponseAt?: string;
  createdAt: string;
  updatedAt: string;
  assigneeId?: string;
  assigneeName?: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  contactTemperature?: string;
  contactCity?: string;
  lastMessage?: { id: string; direction: string; messageType: string; contentText: string; createdAt: string };
  messageCount?: string | number;
}

export interface Message {
  id: string;
  direction: 'inbound' | 'outbound';
  senderKind: string;
  senderId?: string;
  senderName?: string;
  messageType: string;
  contentText: string;
  contentMediaUrl?: string;
  transcript?: string;
  sentimentScore?: number;
  intentClassification?: string;
  deliveredAt?: string;
  readAt?: string;
  createdAt: string;
}

export interface Ticket {
  id: string;
  type: string;
  priority: string;
  intentClassification?: string;
  status: string;
  subject: string;
  csatScore?: number;
  slaResolutionDueAt?: string;
  createdAt: string;
  resolvedAt?: string;
}

export interface Lead {
  id: string;
  stage: string;
  temperature: string;
  tags: string[];
  nextAction?: string;
  nextActionDueAt?: string;
  stageChangedAt: string;
  scores: { prob_purchase_24h?: number; prob_upsell?: number; prob_churn?: number };
  source: { utm_source?: string; campaign_id?: string; creative_id?: string };
  createdAt: string;
  updatedAt: string;
  ownerId?: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  geoCity?: string;
  geoState?: string;
  ownerName?: string;
  lastOrderCents?: string | number;
}

export interface DashboardStats {
  totalContacts: string;
  hotContacts: string;
  activeLeads: string;
  openConversations: string;
  urgentConversations: string;
  openTickets: string;
  orders30d: string;
  revenue30dCents: string;
  revenueTodayCents: string;
  refunds30d: string;
  chargebacks30d: string;
}

export interface DashboardOverview {
  stats: DashboardStats;
  recentOrders: Array<{
    id: string;
    productName: string;
    amountGrossCents: string;
    netCents: string;
    paymentMethod: string;
    status: string;
    approvedAt?: string;
    createdAt: string;
    contactName?: string;
  }>;
}

// ---- Dashboard v2 (inspirado worker) ----
export interface DashboardV2 {
  range: 'today' | 'yesterday' | '7d' | '30d';
  customDate: string | null;
  sales: {
    count: number;
    grossCents: number;
    netCents: number;
    feesCents: number;
    ticketMedioCents: number;
  };
  meta: {
    spendCents: number;
    impressions: number;
    clicks: number;
    ctr: number;
    cpm: number;
  };
  derived: {
    lucroEstimadoCents: number;
    margem: number;
    roas: number;
    cpaCents: number;
  };
  productBreakdown: Array<{ productKind: string; count: number; grossCents: string }>;
  paymentMix: Array<{ paymentMethod: string; count: number; grossCents: string }>;
  takeRate: {
    upsellRate: number;
    bumpRate: number;
    fronts: number;
    upsells: number;
    bumps: number;
  };
  lastSaleAt: string | null;
  testsExcluded: { count: number; grossCents: string };
  refunds: { refunds30d: number; chargebacks30d: number };
  general: {
    totalContacts: number;
    hotContacts: number;
    activeLeads: number;
    openConversations: number;
    urgentConversations: number;
    openTickets: number;
  };
  recentSales: Array<{
    id: string;
    productName: string;
    productKind: string;
    amountGrossCents: string;
    netCents: string;
    paymentMethod: string;
    status: string;
    approvedAt?: string;
    createdAt: string;
    contactName?: string;
    contactEmail?: string;
    utmSource?: string;
    utmCampaign?: string;
  }>;
}

export interface HourlyBreakdown {
  hours: Array<{
    hour: number;
    spendCents: string;
    salesCount: number;
    salesGrossCents: string;
  }>;
}

// ============== Finance ==============
export interface FinanceKpis {
  grossRevenueCents: number;
  netRevenueCents: number;
  gatewayFeesCents: number;
  totalCostsCents: number;
  adSpendCents: number;
  grossProfitCents: number;
  margin: number;
  roas: number;
  approvedOrders: number;
  refundedOrders: number;
  chargebackOrders: number;
}

export interface FinanceOverview {
  month: string;
  kpis: FinanceKpis;
  revenueByProduct: Array<{
    product: string;
    kind: string;
    orders: number;
    grossCents: string;
    netCents: string;
  }>;
  costsByCategory: Array<{
    category: string;
    events: number;
    amountCents: string;
  }>;
  dailyRevenue: Array<{
    day: string;
    grossCents: string;
    netCents: string;
    orders: number;
  }>;
}

export interface JournalEntrySummary {
  id: string;
  competencia: string;
  caixa?: string;
  postedAt: string;
  description: string;
  sourceType: string;
  lockedAt?: string;
  totalDebitCents: string;
  totalCreditCents: string;
  lineCount: number;
}

export interface JournalEntryDetail {
  entry: {
    id: string;
    competencia: string;
    caixa?: string;
    postedAt: string;
    description: string;
    sourceType: string;
    sourceEventId?: string;
    idempotencyKey: string;
    hashSelf: string;
    lockedAt?: string;
    notes?: string;
  };
  lines: Array<{
    id: string;
    debitCents: string;
    creditCents: string;
    product?: string;
    channel?: string;
    campaignId?: string;
    creativeId?: string;
    accountCode: string;
    accountName: string;
    accountType: string;
  }>;
}

export interface CostEvent {
  id: string;
  source: string;
  externalRef?: string;
  occurredAt: string;
  amountCents: string;
  currency: string;
  product?: string;
  channel?: string;
  campaignId?: string;
  creativeId?: string;
  category: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

// ============== Sales ==============
export interface SalesAggregate {
  grossCents: number;
  netCents: number;
  approved: number;
  refunded: number;
  chargeback: number;
}

export interface SaleOrder {
  id: string;
  gateway: string;
  externalId: string;
  productCode: string;
  productName: string;
  productKind: string;
  amountGrossCents: string;
  feeCents: string;
  netCents: string;
  paymentMethod: string;
  installments: number;
  status: string;
  approvedAt?: string;
  refundedAt?: string;
  chargebackAt?: string;
  createdAt: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  geoCity?: string;
  geoState?: string;
}

// ============== Settings ==============
export interface FeatureFlag {
  id: string;
  name: string;
  enabled: boolean;
  rules: Record<string, unknown>;
  description?: string;
  ownerId?: string;
  ownerName?: string;
  createdAt: string;
  updatedAt: string;
}

export interface Integration {
  id: string;
  name: string;
  category: 'core' | 'gateway' | 'channel' | 'llm' | 'observability';
  status: 'online' | 'configured_offline' | 'not_configured';
  latencyMs?: number;
  details?: string;
  required: boolean;
}

export interface SettingsOverview {
  flags: FeatureFlag[];
  integrations: Integration[];
  envMeta: {
    nodeEnv: string;
    logLevel: string;
    timezone: string;
    coreApiUrl: string;
    llmDailyBudgetUsd: number;
    llmMonthlyBudgetUsd: number;
  };
  tenant: {
    id: string;
    slug: string;
    displayName: string;
    cnpj?: string;
    active: boolean;
    metadata: Record<string, unknown>;
    createdAt: string;
  };
  counts: {
    activeUsers: number;
    contacts: number;
    ordersTotal: number;
    journalEntries: number;
    costEvents: number;
    eventsTotal: number;
  };
}

// ============== Funnel ==============
export interface FunnelOverview {
  month: string;
  funnel: Array<{
    stage: string;
    count: number;
    dropoffPct: number | null;
  }>;
  dropouts: Array<{ stage: string; count: number }>;
  kpis: {
    total: number;
    topOfFunnel: number;
    customers: number;
    wonCount: number;
    lostCount: number;
    checkoutToCustomerRate: number;
    revenueCents: number;
    ordersApproved: number;
  };
  transitions: Array<{ toStage: string; n: number }>;
}

export interface FunnelStageContacts {
  stage: string;
  count: number;
  contacts: Array<{
    id: string;
    lifecycleStage: string;
    temperature: string;
    createdAt: string;
    lastSeenAt?: string;
    displayName?: string;
    emailDisplay?: string;
    geoCity?: string;
    geoState?: string;
    utmSource?: string;
    utmCampaign?: string;
    ordersCount: number;
    ltvCents: string;
  }>;
}

// ============== Ops / Anomalies ==============
export interface Anomaly {
  id: string;
  detector: string;
  severity: 'critical' | 'high' | 'warning' | 'info';
  detectedAt: string;
  dimensionKeys: Record<string, unknown>;
  description: string;
  rootCauseAnalysis?: string;
  suggestedAction?: string;
  estimatedImpactCents?: string;
  status: 'open' | 'acknowledged' | 'resolved';
  resolvedAt?: string;
  resolvedByName?: string;
}

export interface OpsOverview {
  anomalies: Anomaly[];
  health: {
    db: { ok: boolean; latencyMs: number; error?: string };
    redis: { ok: boolean; latencyMs: number; error?: string };
    lastEventAt?: string;
    lastOrderAt?: string;
    lastMessageAt?: string;
    lastTictoWebhookAt?: string;
  };
  slaBreaches: {
    ticketsFirstResponse: number;
    ticketsResolution: number;
    openTickets: number;
    openUrgent: number;
    conversationsNoResponse2h: number;
    conversationsOpenUrgentNoResponse: number;
    recoveryStale30min: number;
    recoveryQueueSize: number;
  };
  todayStats: {
    eventsToday: number;
    salesToday: number;
    revenueTodayCents: number;
  };
  openByCriticality: {
    critical: number;
    high: number;
    warning: number;
    info: number;
  };
}

// ============== Recovery ==============
export interface RecoveryLead {
  id: string;
  stage: string;
  temperature: string;
  stageChangedAt: string;
  nextAction?: string;
  nextActionDueAt?: string;
  scores: { prob_purchase_24h?: number; prob_churn?: number; prob_upsell?: number };
  tags: string[];
  source: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
  contactId: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  geoCity?: string;
  geoState?: string;
  utmSource?: string;
  utmCampaign?: string;
  ownerName?: string;
  ownerId?: string;
  urgency: number;
  secondsSinceStage: number;
  priorOrders: number;
  lastAttemptCents?: string;
  openConversationId?: string;
  lastChannel?: string;
  lastTouchAt?: string;
  abandonStatus?: string;
  abandonPaymentMethod?: string;
  abandonProductKind?: string;
  whatsappLink?: string;
  abandonOrderId?: string;
  priorLtvCents?: string;
  lastPurchaseAt?: string;
  openAttempts?: number;
  whatsappCount?: number;
  whatsappLastAt?: string | null;
  emailCount?: number;
  emailLastAt?: string | null;
  callCount?: number;
  callLastAt?: string | null;
  callContactCount?: number;
  tpLastTouchAt?: string | null;
  hasOpenConversation?: boolean;
}

export interface RecoveryQueue {
  leads: RecoveryLead[];
  summary: {
    total: number;
    checkoutCount: number;
    paymentPendingCount: number;
    paymentFailedCount: number;
    hotCount: number;
    potentialValueCents: number;
  };
}

// ============== Team ==============
export interface TeamUserKpi {
  id: string;
  email: string;
  fullName: string;
  role: string;
  regime?: string;
  hireDate?: string;
  commissionsCount: number;
  commissionsPending: number;
  commissionsPaid: number;
  commissionsCancelled: number;
  earnedCents: string;
  pendingCents: string;
  paidCents: string;
  messagesSent: number;
  ticketsTotal: number;
  ticketsResolved: number;
  avgCsat?: string;
}

export interface TeamOverview {
  month: string;
  users: TeamUserKpi[];
  totals: {
    users: number;
    totalCommissions: number;
    totalPayoutCents: number;
    pendingPayoutCents: number;
  };
}

export interface TeamUserDetail {
  user: {
    id: string;
    email: string;
    fullName: string;
    role: string;
    regime?: string;
    hireDate?: string;
    baseSalaryCents?: string;
    metadata?: Record<string, unknown>;
  };
  month: string;
  summary: {
    commissionsCount: number;
    pendingCount: number;
    paidCount: number;
    cancelledCount: number;
    earnedCents: number;
    pendingCents: number;
    paidCents: number;
    cancelledCents: number;
  };
  commissions: Array<{
    id: string;
    baseCents: string;
    percent: string;
    amountCents: string;
    status: string;
    liberationDueDate?: string;
    paidAt?: string;
    createdAt: string;
    orderId: string;
    productName: string;
    amountGrossCents: string;
    paymentMethod: string;
    orderStatus: string;
    approvedAt?: string;
    contactName?: string;
    planName: string;
    baseCalc: string;
    structure: string;
  }>;
  recentMessages: Array<{
    id: string;
    direction: string;
    messageType: string;
    contentText: string;
    createdAt: string;
    channel: string;
    contactName?: string;
  }>;
  tickets: Array<{
    id: string;
    type: string;
    priority: string;
    status: string;
    subject: string;
    csatScore?: number;
    createdAt: string;
    resolvedAt?: string;
  }>;
}

// ============== Traffic ==============
export interface TrafficKpis {
  spendCents: number;
  grossCents: number;
  netCents: number;
  approvedOrders: number;
  roas: number;
  cpaCents: number;
  margin: number;
}

export interface TrafficCampaign {
  campaign: string;
  campaignName?: string;
  spendCents: string;
  creatives: number;
  contacts: number;
  orders: number;
  grossCents: string;
  netCents: string;
}

export interface TrafficCreative {
  campaign: string;
  creative: string;
  campaignName?: string;
  adName?: string;
  spendCents: string;
  events: number;
  contacts: number;
  orders: number;
  grossCents: string;
}

export interface TrafficSource {
  source: string;
  contacts: number;
  orders: number;
  grossCents: string;
}

export interface TrafficOverview {
  month: string;
  kpis: TrafficKpis;
  campaigns: TrafficCampaign[];
  creatives: TrafficCreative[];
  sources: TrafficSource[];
}

export interface CampaignSummary {
  spendCents: number;
  grossCents: number;
  netCents: number;
  events: number;
  creatives: number;
  contacts: number;
  orders: number;
  roas: number;
  cpaCents: number;
  conversionRate: number;
}

export interface CampaignDetail {
  campaign: string;
  month: string;
  summary: CampaignSummary;
  creatives: Array<{
    creative: string;
    spendCents: string;
    events: number;
    contacts: number;
    orders: number;
    grossCents: string;
  }>;
  recentContacts: Array<{
    id: string;
    displayName?: string;
    emailDisplay?: string;
    geoCity?: string;
    geoState?: string;
    lifecycleStage: string;
    temperature: string;
    createdAt: string;
    ordersCount: number;
    ltvCents: string;
  }>;
}

export interface SaleDetail {
  order: SaleOrder & {
    parentOrderId?: string;
    scheduledReleaseAt?: string;
    releasedAt?: string;
    withdrawnAt?: string;
    updatedAt: string;
    contactEmailReal?: string;
    contactPhoneReal?: string;
  };
  journalEntries: Array<{
    id: string;
    competencia: string;
    description: string;
    postedAt: string;
    totalDebitCents: string;
  }>;
  upsellChain: Array<{
    id: string;
    productName: string;
    productKind: string;
    amountGrossCents: string;
    status: string;
    createdAt: string;
  }>;
}

// ============== Endpoints ==============
export const apiClient = {
  health: () => api<{ status: string; checks: Record<string, string> }>('/health'),

  dashboardOverview: () => api<DashboardOverview>('/dashboard/overview'),

  dashboardV2: (params: { range?: 'today' | 'yesterday' | '7d' | '30d'; date?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.range) q.set('range', params.range);
    if (params.date) q.set('date', params.date);
    return api<DashboardV2>(`/dashboard/overview${q.toString() ? '?' + q : ''}`);
  },

  dashboardHourly: (date?: string) =>
    api<HourlyBreakdown>(`/dashboard/hourly${date ? `?date=${date}` : ''}`),

  contactsList: (params: { temperature?: string; stage?: string; search?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.temperature) q.set('temperature', params.temperature);
    if (params.stage) q.set('stage', params.stage);
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return api<{ contacts: Contact[]; total: number; limit: number; offset: number }>(
      `/contacts${q.toString() ? '?' + q : ''}`,
    );
  },

  contactDetail: (id: string) =>
    api<{
      contact: ContactDetail;
      timeline: ContactEvent[];
      orders: Order[];
      conversations: Conversation[];
      tickets: Ticket[];
      scores: Array<{ modelName: string; score: number; scoredAt: string }>;
    }>(`/contacts/${id}`),

  conversations: (params: { status?: string; priority?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.priority) q.set('priority', params.priority);
    if (params.limit) q.set('limit', String(params.limit));
    return api<{ conversations: Conversation[]; total: number }>(
      `/conversations${q.toString() ? '?' + q : ''}`,
    );
  },

  conversationMessages: (id: string) =>
    api<{ conversation: Conversation; messages: Message[] }>(`/conversations/${id}/messages`),

  sendMessage: (id: string, content_text: string) =>
    api<{ message: Message }>(`/conversations/${id}/messages`, {
      method: 'POST',
      body: JSON.stringify({ content_text, message_type: 'text' }),
    }),

  aiSuggest: (id: string) =>
    api<{
      suggestion: string;
      fallback: boolean;
      fallbackReason: string | null;
      usage: { costUsdCents: number; latencyMs: number } | null;
      basedOnMessages: number;
    }>(`/conversations/${id}/ai-suggest`, { method: 'POST', body: '{}' }),

  pipeline: () =>
    api<{ stages: string[]; byStage: Record<string, Lead[]>; total: number }>('/pipeline'),

  moveLeadStage: (id: string, stage: string) =>
    api<{ lead: Lead }>(`/leads/${id}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage }),
    }),

  financeOverview: (month?: string) =>
    api<FinanceOverview>(`/finance/overview${month ? `?month=${month}` : ''}`),

  journalEntries: (params: { limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return api<{
      entries: JournalEntrySummary[];
      total: number;
      limit: number;
      offset: number;
    }>(`/finance/journal-entries${q.toString() ? '?' + q : ''}`);
  },

  journalEntryDetail: (id: string) =>
    api<JournalEntryDetail>(`/finance/journal-entries/${id}`),

  costEvents: (params: { month?: string; source?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.month) q.set('month', params.month);
    if (params.source) q.set('source', params.source);
    if (params.limit) q.set('limit', String(params.limit));
    return api<{
      events: CostEvent[];
      totals: Array<{ category: string; events: number; amountCents: string }>;
    }>(`/finance/cost-events${q.toString() ? '?' + q : ''}`);
  },

  sales: (params: {
    status?: string;
    payment_method?: string;
    product_kind?: string;
    month?: string;
    search?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.status) q.set('status', params.status);
    if (params.payment_method) q.set('payment_method', params.payment_method);
    if (params.product_kind) q.set('product_kind', params.product_kind);
    if (params.month) q.set('month', params.month);
    if (params.search) q.set('search', params.search);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.offset) q.set('offset', String(params.offset));
    return api<{
      orders: SaleOrder[];
      total: number;
      limit: number;
      offset: number;
      aggregate: SalesAggregate;
    }>(`/sales${q.toString() ? '?' + q : ''}`);
  },

  saleDetail: (id: string) => api<SaleDetail>(`/sales/${id}`),

  syncTicto: (params: { since?: string; until?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.since) q.set('since', params.since);
    if (params.until) q.set('until', params.until);
    return api<{
      ok: boolean;
      totalFetched: number;
      totalContactsCreated: number;
      totalOrdersInserted: number;
      totalOrdersSkipped: number;
      totalErrors: number;
      since: string;
      until: string;
      durationMs: number;
      dates: Array<{
        date: string;
        fetched: number;
        contactsCreated: number;
        ordersInserted: number;
        ordersSkipped: number;
        errors: string[];
      }>;
    }>(`/sales/sync-ticto${q.toString() ? '?' + q : ''}`, {
      method: 'POST',
      body: '{}',
    });
  },

  salesLastSyncTicto: () =>
    api<{ totalOrders: number; realOrders: number; lastUpdated: string | null }>(
      '/sales/last-sync-ticto',
    ),

  backfillLedger: () =>
    api<{
      ok: boolean;
      ordersScanned: number;
      jesCreated: number;
      jesSkipped: number;
      eventsCreated: number;
      eventsSkipped: number;
      errors: string[];
      durationMs: number;
    }>('/sales/backfill-ledger', { method: 'POST', body: '{}' }),

  markRefunded: (orderId: string, payload: { reason?: string; refundedAt?: string }) =>
    api<{
      ok: boolean;
      orderId: string;
      externalId: string;
      reverseJeId?: string;
      commissionsCancelled: number;
      eventId?: string;
      skipped?: string;
      error?: string;
    }>(`/sales/${orderId}/mark-refunded`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  schedulerStatus: () =>
    api<{
      enabled: boolean;
      intervals: { ticto_sync: number; backfill_ledger: number; meta_sync: number; abandoned_sync?: number; recovery_reconcile?: number };
      lastRuns: Array<{
        jobName: string;
        startedAt: string;
        finishedAt: string | null;
        durationMs: number | null;
        status: string;
        trigger: string;
        summary: Record<string, unknown>;
        error: string | null;
      }>;
    }>('/sales/scheduler'),

  schedulerRun: (job: 'ticto_sync' | 'backfill_ledger' | 'meta_sync' | 'abandoned_sync' | 'recovery_reconcile') =>
    api<{ ok: boolean; summary?: Record<string, unknown>; error?: string; durationMs: number }>(
      `/sales/scheduler/run/${job}`,
      { method: 'POST', body: '{}' },
    ),

  callStart: (payload: { contact_id: string; lead_id?: string }) =>
    api<{ call_id: string; started_at: string }>('/calls/start', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  callComplete: (
    id: string,
    payload: { outcome: string; notes?: string; duration_seconds?: number },
  ) =>
    api<{ call: { id: string; outcome: string; duration_seconds: number } }>(
      `/calls/${id}/complete`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),

  callStats: () =>
    api<{ today: number; last7d: number; last30d: number; willPay30d: number; contactMade30d: number }>(
      '/calls/stats',
    ),

  callsRecent: (limit = 50) =>
    api<{ calls: Array<{ id: string; startedAt: string; endedAt: string | null; durationSeconds: number | null; outcome: string | null; notes: string | null; contactId: string; leadId: string | null; contactName: string | null; contactPhone: string | null }> }>(
      `/calls/recent?limit=${limit}`,
    ),

  recoveryAttribute: (leadId: string, userId?: string) =>
    api<{ ok: boolean; attributedOrders: number }>(`/recovery/leads/${leadId}/attribute`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  meOverview: () =>
    api<{
      queue: { openTotal: number; hot: number };
      myClients: { clientsCount: number; ordersCount: number; orders7d: number; orders30d: number };
      recoveries: { won7d: number; won30d: number };
      calls: { today: number; last30d: number; willPay30d: number };
    }>('/users/me/overview'),

  meClients: (params?: { limit?: number; sort?: 'recent' | 'oldest' }) => {
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.sort) q.set('sort', params.sort);
    return api<{
      clients: Array<{
        contactId: string;
        name: string | null;
        email: string | null;
        phone: string | null;
        lifecycleStage: string;
        orderCount: number;
        kinds: string[];
        firstPurchaseAt: string;
        lastPurchaseAt: string;
        daysSinceLast: string;
        orders: Array<{
          order_id: string;
          kind: string;
          product_name: string;
          payment_method: string;
          approved_at: string;
          external_id: string;
        }>;
        nextAction: string;
        nextActionPriority: 'high' | 'medium' | 'low';
      }>;
    }>(`/users/me/clients${q.toString() ? '?' + q : ''}`);
  },

  aiDraftMessage: (leadId: string, payload?: { tone?: string; extra?: string }) =>
    api<{
      message: string;
      fallback: boolean;
      reason?: string;
      warning?: string;
      usage?: { inputTokens: number; outputTokens: number; costUsdCents: number; latencyMs: number };
    }>(`/ai/draft-message/${leadId}`, {
      method: 'POST',
      body: JSON.stringify(payload ?? {}),
    }),

  aiCustomerSummary: (contactId: string) =>
    api<{
      summary: string;
      fallback: boolean;
      reason?: string;
      warning?: string;
      usage?: { inputTokens: number; outputTokens: number; costUsdCents: number; latencyMs: number };
    }>(`/ai/customer-summary/${contactId}`),

  aiUsage: () =>
    api<{ today: { calls: number; inputTokens: number; outputTokens: number; costUsdCents: number } }>(
      '/ai/usage',
    ),

  meCommissions: () =>
    api<{
      totals: { pendingCents: number; paidCents: number; cancelledCents: number };
      byPlan: Array<{ plan: string; status: string; count: number; totalCents: number }>;
      items: Array<{
        id: string;
        amountCents: number;
        percent: string;
        baseCents: number;
        status: string;
        liberationDueDate: string | null;
        paidAt: string | null;
        clawbackReason: string | null;
        createdAt: string;
        plan: string;
        order: { externalId: string; productName: string; productKind: string; grossCents: number };
        contactName: string;
      }>;
    }>('/users/me/commissions'),

  trafficOverview: (month?: string) =>
    api<TrafficOverview>(`/traffic/overview${month ? `?month=${month}` : ''}`),

  campaignDetail: (campaign: string, month?: string) =>
    api<CampaignDetail>(
      `/traffic/campaigns/${encodeURIComponent(campaign)}${month ? `?month=${month}` : ''}`,
    ),

  syncMeta: (params: { since?: string; until?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.since) q.set('since', params.since);
    if (params.until) q.set('until', params.until);
    return api<{
      ok: boolean;
      adAccountId: string;
      dateRange: { since: string | null; until: string | null };
      insightsAdDay: number;
      insertedAds: number;
      uniqueCampaigns: number;
      uniqueAds: number;
      totalSpendCents: number;
      durationMs: number;
    }>(`/traffic/sync-meta${q.toString() ? '?' + q : ''}`, {
      method: 'POST',
      body: '{}',
    });
  },

  trafficLastSync: () =>
    api<{ lastDataDate: string | null; lastSyncedAt: string | null; totalEvents: number }>(
      '/traffic/last-sync',
    ),

  teamOverview: (month?: string) =>
    api<TeamOverview>(`/team/overview${month ? `?month=${month}` : ''}`),

  teamUserDetail: (id: string, month?: string) =>
    api<TeamUserDetail>(`/team/users/${id}${month ? `?month=${month}` : ''}`),

  recoveryQueue: (params: { stage?: string; temperature?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.stage) q.set('stage', params.stage);
    if (params.temperature) q.set('temperature', params.temperature);
    if (params.limit) q.set('limit', String(params.limit));
    return api<RecoveryQueue>(`/recovery/queue${q.toString() ? '?' + q : ''}`);
  },

  recoveryTouch: (id: string, body: { kind: 'whatsapp' | 'email' | 'call' | 'note'; note?: string; next_action?: string }) =>
    api<{ ok: boolean; kind: string; lead_id: string }>(`/recovery/leads/${id}/touch`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  recoveryDismiss: (id: string, reason: 'recovered' | 'lost' | 'manual') =>
    api<{ lead: { id: string; stage: string }; reason: string }>(
      `/recovery/leads/${id}/dismiss`,
      { method: 'POST', body: JSON.stringify({ reason }) },
    ),

  opsOverview: () => api<OpsOverview>('/ops/overview'),

  ackAnomaly: (id: string, action: 'acknowledge' | 'resolve') =>
    api<{ anomaly: { id: string; status: string; resolvedAt?: string } }>(
      `/ops/anomalies/${id}/ack`,
      { method: 'POST', body: JSON.stringify({ action }) },
    ),

  funnelOverview: (month?: string) =>
    api<FunnelOverview>(`/funnel/overview${month ? `?month=${month}` : ''}`),

  funnelStageContacts: (stage: string, limit?: number) =>
    api<FunnelStageContacts>(
      `/funnel/stage/${encodeURIComponent(stage)}${limit ? `?limit=${limit}` : ''}`,
    ),

  settingsOverview: () => api<SettingsOverview>('/settings/overview'),

  toggleFeatureFlag: (id: string, enabled: boolean) =>
    api<{ flag: { id: string; name: string; enabled: boolean; updatedAt: string } }>(
      `/settings/feature-flags/${id}`,
      { method: 'PATCH', body: JSON.stringify({ enabled }) },
    ),

  // ============== WhatsApp / Evolution API ==============
  whatsappInstances: () =>
    api<{ instances: WhatsAppInstance[] }>('/whatsapp/instances'),

  whatsappInstance: (id: string) =>
    api<{ instance: WhatsAppInstance }>(`/whatsapp/instances/${id}`),

  whatsappConnect: (id: string) =>
    api<{ ok: boolean; status: string; qrBase64: string | null; qrCode: string | null }>(
      `/whatsapp/instances/${id}/connect`,
      { method: 'POST' },
    ),

  whatsappQr: (id: string) =>
    api<{ status: string; qrBase64: string | null; phoneNumber: string | null }>(
      `/whatsapp/instances/${id}/qr`,
    ),

  whatsappDisconnect: (id: string) =>
    api<{ ok: boolean }>(`/whatsapp/instances/${id}/disconnect`, { method: 'POST' }),

  whatsappSendTest: (id: string, number: string, text: string) =>
    api<{ ok: boolean }>(`/whatsapp/instances/${id}/send-test`, {
      method: 'POST',
      body: JSON.stringify({ number, text }),
    }),

  whatsappHealth: () =>
    api<{
      evolution: { ok: boolean; version?: string; latencyMs?: number; error?: string };
      instances: { total: number; connected: number };
    }>('/whatsapp/health'),
};

export interface WhatsAppInstance {
  id: string;
  name: string;
  instanceName: string;
  purpose: string;
  status: 'disconnected' | 'connecting' | 'qr' | 'connected' | 'error';
  channelType?: 'baileys' | 'cloud';
  phoneNumber: string | null;
  phoneNumberId?: string | null;
  businessAccountId?: string | null;
  displayPhoneNumber?: string | null;
  profileName: string | null;
  lastConnectedAt: string | null;
  lastError: string | null;
  defaultAssigneeId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface WhatsAppTemplate {
  id: string;
  externalId: string;
  name: string;
  language: string;
  category: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | 'PAUSED' | 'DISABLED';
  previewText: string | null;
  rejectionReason: string | null;
  lastSyncedAt: string;
}

export interface WhatsAppCloudSetupInfo {
  webhookUrl: string;
  verifyToken: string | null;
  graphApiVersion: string;
  subscribeFields: string[];
  publicUrlConfigured: boolean;
  warningIfLocalhost: string | null;
}

export const whatsappCloudClient = {
  createInstance: (data: { name: string; purpose: string; default_assignee_id?: string }) =>
    api<{ ok: boolean; id: string }>(`/whatsapp/cloud/instances`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  saveCredentials: (
    id: string,
    creds: {
      phone_number_id: string;
      business_account_id: string;
      access_token: string;
      app_secret: string;
      verify_token: string;
      graph_api_version?: string;
    },
  ) =>
    api<{ ok: boolean; status: string; health?: unknown; error?: string }>(
      `/whatsapp/cloud/instances/${id}/credentials`,
      { method: 'PUT', body: JSON.stringify(creds) },
    ),

  health: (id: string) =>
    api<{ ok: boolean; phoneNumber?: string; qualityRating?: string; error?: string; latencyMs: number }>(
      `/whatsapp/cloud/instances/${id}/health`,
    ),

  setupInfo: (id: string) =>
    api<WhatsAppCloudSetupInfo>(`/whatsapp/cloud/instances/${id}/setup-info`),

  syncTemplates: (id: string) =>
    api<{ ok: boolean; total: number; upserted: number }>(
      `/whatsapp/cloud/instances/${id}/templates/sync`,
      { method: 'POST' },
    ),

  templates: (id: string) =>
    api<{ templates: WhatsAppTemplate[] }>(`/whatsapp/cloud/instances/${id}/templates`),

  testSend: (
    id: string,
    payload: {
      to: string;
      mode: 'text' | 'template';
      text?: string;
      template_name?: string;
      template_language?: string;
    },
  ) =>
    api<{ ok: boolean; response: unknown }>(
      `/whatsapp/cloud/instances/${id}/test-send`,
      { method: 'POST', body: JSON.stringify(payload) },
    ),
};
